import { useState, useCallback } from 'react';
import httpClient from '../services/httpInterceptor';

interface ApiState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

// Custom hook for API operations
export const useApi = <T = any>() => {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const resetState = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  // Generic request function
  const executeRequest = useCallback(async (requestFn: () => Promise<any>) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await requestFn();
      setState({
        data: response.data,
        loading: false,
        error: null,
      });
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || 'An error occurred';
      setState({
        data: null,
        loading: false,
        error: errorMessage,
      });
      throw error;
    }
  }, []);

  // GET method
  const get = useCallback(
    (endpoint: string, params?: Record<string, any>) => {
      return executeRequest(() => httpClient.get(endpoint, { params }));
    },
    [executeRequest]
  );

  // POST method
  const post = useCallback(
    (endpoint: string, data?: any, config?: any) => {
      return executeRequest(() => httpClient.post(endpoint, data, config));
    },
    [executeRequest]
  );

  // PUT method
  const put = useCallback(
    (endpoint: string, data?: any, config?: any) => {
      return executeRequest(() => httpClient.put(endpoint, data, config));
    },
    [executeRequest]
  );

  // DELETE method
  const del = useCallback(
    (endpoint: string, config?: any) => {
      return executeRequest(() => httpClient.delete(endpoint, config));
    },
    [executeRequest]
  );

  // PATCH method
  const patch = useCallback(
    (endpoint: string, data?: any, config?: any) => {
      return executeRequest(() => httpClient.patch(endpoint, data, config));
    },
    [executeRequest]
  );

  return {
    ...state,
    get,
    post,
    put,
    delete: del,
    patch,
    resetState,
  };
};

// Utility functions for direct API calls (without state management)
export const apiUtils = {
  // GET request
  get: async <T = any>(
    endpoint: string,
    params?: Record<string, any>,
    signal?: AbortSignal
  ): Promise<ApiResponse<T>> => {
    const response = await httpClient.get(endpoint, { params, signal });
    return response;
  },

  // POST request
  post: async <T = any>(
    endpoint: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> => {
    const response = await httpClient.post(endpoint, data, config);
    return response;
  },

  // PUT request
  put: async <T = any>(
    endpoint: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> => {
    const response = await httpClient.put(endpoint, data, config);
    return response.data;
  },

  // DELETE request
  delete: async <T = any>(
    endpoint: string,
    config?: any
  ): Promise<ApiResponse<T>> => {
    const response = await httpClient.delete(endpoint, config);
    return response.data;
  },

  // PATCH request
  patch: async <T = any>(
    endpoint: string,
    data?: any,
    config?: any
  ): Promise<ApiResponse<T>> => {
    const response = await httpClient.patch(endpoint, data, config);
    return response.data;
  },
};

// Specialized hooks for common patterns
export const useApiData = <T = any>(
  endpoint: string,
  params?: Record<string, any>
) => {
  const api = useApi<T>();

  const fetchData = useCallback(() => {
    return api.get(endpoint, params);
  }, [api, endpoint, params]);

  return {
    ...api,
    fetchData,
  };
};

export const useApiMutation = <T = any>() => {
  const api = useApi<T>();

  return {
    ...api,
    mutate: {
      create: api.post,
      update: api.put,
      delete: api.delete,
      patch: api.patch,
    },
  };
};
